import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';
import { kanbanService } from '@/features/kanban/services/kanban.service';
import { dailyTasksService } from '@/features/daily-tasks/services/daily-tasks.service';
import { tasksBoardService } from '@/features/kanban/services/tasks-board.service';
import { useTasksBoardByEmail } from '@/features/kanban/hooks/useTasksBoardByEmail';
import { BoardFilterModal } from '@/features/dashboard/components/BoardFilterModal';
import { NotionQueueColumn } from '@/features/dashboard/components/NotionQueueColumn';
import { PomodoroColumn } from '@/features/dashboard/components/PomodoroColumn';
import { CompletedTasksColumn } from '@/features/dashboard/components/CompletedTasksColumn';
import { CalendarColumn } from '@/features/dashboard/components/CalendarColumn';
import { UpcomingDeadlinesColumn } from '@/features/dashboard/components/UpcomingDeadlinesColumn';
import { RecurringClientTasksColumn } from '@/features/dashboard/components/RecurringClientTasksColumn';
import { PlaybookMyStepsColumn } from '@/features/dashboard/components/PlaybookMyStepsColumn';
import { TextQueuesView } from '@/features/dashboard/components/TextQueuesView';
import { usePermissions } from '@/common/hooks/usePermissions';
import { Permission } from '@/common/enums/permissions.enum';
import { Button } from '@/components/ui/button';
import { StickyHorizontalScrollbar } from '@/components/ui/sticky-horizontal-scrollbar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { sortNotionTasksByDueDate } from '@/features/kanban/utils/sortNotionTasksByDueDate';

export const Route = createFileRoute('/_authenticated/my-dashboard')({
  component: MyDashboardPage,
});

function MyDashboardPage() {
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canSeeRecurringTasks = hasPermission(Permission.RECURRING_CLIENT_TASKS_VIEW);
  const [selectedBoardIds, setSelectedBoardIds] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // Buscar boards
  const { data: boards = [] } = useQuery({
    queryKey: ['notion-boards'],
    queryFn: () => kanbanService.getMyBoards(),
  });

  // Buscar coluna do usuário logado usando tasks-board/by-email
  const {
    data: tasksBoardData,
    isLoading: loadingNotion,
    refetch,
    isSocketConnected,
    lastUpdate,
  } = useTasksBoardByEmail();

  // Verificar se há colunas de responsabilidade para mostrar a tab
  const { data: responsibilityData } = useQuery({
    queryKey: ['tasks-board-by-responsibility'],
    queryFn: () => tasksBoardService.getTasksBoardByResponsibility(),
  });

  const hasResponsibilityQueues =
    responsibilityData?.columns && responsibilityData.columns.length > 0;

  // Obter a coluna retornada (deve ter apenas uma coluna com o email do usuário)
  const userColumn = useMemo(() => {
    const columns = tasksBoardData && Array.isArray((tasksBoardData as any).columns)
      ? (tasksBoardData as any).columns
      : [];
    // Deve retornar apenas uma coluna, mas pegamos a primeira se houver múltiplas
    return columns.length > 0 ? columns[0] : null;
  }, [tasksBoardData]);

  // Filtrar tarefas baseado nos boards selecionados
  const filteredTasks = useMemo(() => {
    if (!userColumn || !userColumn.tasks) return [];

    const allTasks = userColumn.tasks;
    const safeSelectedBoardIds = selectedBoardIds || [];

    // Se nenhum board selecionado, mostrar todas as tarefas
    if (safeSelectedBoardIds.length === 0) {
      return sortNotionTasksByDueDate<any>(allTasks);
    }

    // Filtrar tarefas pelos boards selecionados
    const tasksByBoards = allTasks.filter((task: any) => {
      const taskBoardId = task.boardId;
      return taskBoardId && safeSelectedBoardIds.includes(taskBoardId);
    });
    return sortNotionTasksByDueDate<any>(tasksByBoards);
  }, [userColumn, selectedBoardIds]);

  // Função para verificar se a tarefa está aguardando aprovação
  const isWaitingApproval = (task: any) => {
    return (
      task.textCheckbox === true &&
      task.artCheckbox === true &&
      task.clientApprovalCheckbox === false
    );
  };

  // Separar tarefas: "Para executar" e "Aguardando Aprovação"
  // Regra: tudo que aparece no quadro de tarefas (para o usuário) deve aparecer em "Para executar",
  // exceto o que está aguardando aprovação do cliente.
  const tasksToExecute = useMemo(
    () => filteredTasks.filter((task: any) => !isWaitingApproval(task)),
    [filteredTasks],
  );
  const tasksWaitingApproval = useMemo(
    () => filteredTasks.filter((task: any) => isWaitingApproval(task)),
    [filteredTasks],
  );

  // Buscar tarefas diárias
  const { data: dailyTasks = [], isLoading: loadingDaily } = useQuery({
    queryKey: ['daily-tasks'],
    queryFn: () => dailyTasksService.getTasks(),
  });

  // Calendário da equipe: só na coluna Calendário (team-calendar), não no Pomodoro
  const dailyTasksWithoutTeamCalendar = useMemo(
    () => dailyTasks.filter((t) => t.isTeamCalendar !== true),
    [dailyTasks],
  );

  // Separar tarefas ativas e concluídas
  const activeDailyTasks = useMemo(
    () => dailyTasksWithoutTeamCalendar.filter((t) => t.status !== 'done'),
    [dailyTasksWithoutTeamCalendar],
  );
  const completedDailyTasks = useMemo(
    () =>
      dailyTasks
        .filter((t) => t.status === 'done')
        .sort((a, b) => {
          if (!a.completedAt || !b.completedAt) return 0;
          return new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime();
        }),
    [dailyTasks],
  );

  // Formatar última atualização
  const lastUpdateText = useMemo(() => {
    if (!lastUpdate) return 'Nunca atualizado';
    try {
      return formatDistanceToNow(new Date(lastUpdate), {
        addSuffix: true,
        locale: ptBR,
      });
    } catch {
      return 'Agora';
    }
  }, [lastUpdate]);

  const handleBoardFilterChange = (boardIds: string[]) => {
    setSelectedBoardIds(boardIds);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const dashboardQueryKeys = [
        ['daily-tasks'],
        ['playbook-my-tasks'],
        ['team-calendar-tasks'],
        ['team-calendar-settings'],
        ['calendar-dashboard-events'],
        ['recurring-client-tasks-my'],
        ['tasks-board-by-responsibility'],
      ] as const;

      await Promise.all([
        refetch(),
        ...dashboardQueryKeys.map((queryKey) =>
          queryClient.invalidateQueries({ queryKey }),
        ),
      ]);

      await Promise.all(
        dashboardQueryKeys.map((queryKey) =>
          queryClient.refetchQueries({ queryKey }),
        ),
      );
    } finally {
      setIsRefreshing(false);
    }
  };

  const refreshBusy = isRefreshing || loadingNotion || loadingDaily;

  // Quando os boards carregarem pela primeira vez, selecionar todos por padrão
  useEffect(() => {
    if (boards.length > 0 && selectedBoardIds.length === 0) {
      setSelectedBoardIds(boards.map((b: any) => b.id));
    }
  }, [boards, selectedBoardIds.length]);

  // Handlers para drag-to-scroll
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!scrollContainerRef.current) return;
    
    // Verificar se o clique foi em um elemento interativo (botão, link, input, etc)
    const target = e.target as HTMLElement;
    const isInteractiveElement =
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input') ||
      target.closest('textarea') ||
      target.closest('select') ||
      target.closest('[role="button"]') ||
      target.closest('[draggable="true"]') ||
      target.closest('.task-item') ||
      target.closest('[data-draggable]');
    
    // Se for um elemento interativo, não iniciar o drag do scroll
    if (isInteractiveElement) return;
    
    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
    scrollContainerRef.current.style.cursor = 'grabbing';
    scrollContainerRef.current.style.userSelect = 'none';
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.cursor = 'grab';
      scrollContainerRef.current.style.userSelect = 'auto';
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.cursor = 'grab';
      scrollContainerRef.current.style.userSelect = 'auto';
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Velocidade do scroll (ajustável)
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Minha Dashboard</h1>
          <p className="text-muted-foreground">Gerencie suas tarefas e produtividade</p>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {isSocketConnected ? (
              <Wifi className="h-4 w-4 text-green-500" />
            ) : (
              <WifiOff className="h-4 w-4 text-red-500" />
            )}
            <span>
              {isSocketConnected ? 'Conectado' : 'Desconectado'}
              {lastUpdate && ` • Última atualização: ${lastUpdateText}`}
              {!isSocketConnected && ' • Atualizando a cada 30s'}
            </span>
          </div>
          <Button
            onClick={() => void handleRefresh()}
            disabled={refreshBusy}
            variant="outline"
            size="sm"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshBusy ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="flex items-center gap-4 flex-wrap mb-6">
          <TabsList>
            <TabsTrigger value="dashboard">Minha Dashboard</TabsTrigger>
            {hasResponsibilityQueues && (
              <TabsTrigger value="responsibility">Filas de tarefas que gerencio</TabsTrigger>
            )}
          </TabsList>
          {activeTab === 'dashboard' && (
            <BoardFilterModal
              boards={boards || []}
              selectedBoardIds={
                selectedBoardIds.length > 0 
                  ? selectedBoardIds 
                  : (boards || []).map((b: any) => b.id).filter((id: any) => id != null)
              }
              onChange={handleBoardFilterChange}
            />
          )}
        </div>

        <TabsContent value="dashboard" className="space-y-6">
          <div>
            <div
              ref={scrollContainerRef}
              className="overflow-x-auto cursor-grab active:cursor-grabbing [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              onMouseDown={handleMouseDown}
              onMouseLeave={handleMouseLeave}
              onMouseUp={handleMouseUp}
              onMouseMove={handleMouseMove}
            >
              <div className="flex gap-6 min-w-max pb-4">
                {tasksToExecute.length > 0 && (
                  <NotionQueueColumn
                    title="Tarefas do Notion"
                    tasks={tasksToExecute}
                    isLoading={loadingNotion}
                    dailyTasks={dailyTasks}
                    showTasksBoardLink
                    onTaskDrag={() => {
                      // Handler vazio - o drag and drop é gerenciado pela coluna Pomodoro
                    }}
                  />
                )}
                {tasksWaitingApproval.length > 0 && (
                  <NotionQueueColumn
                    title="Aguardando Aprovação"
                    tasks={tasksWaitingApproval}
                    isLoading={loadingNotion}
                    dailyTasks={dailyTasks}
                    onTaskDrag={() => {
                      // Handler vazio - o drag and drop é gerenciado pela coluna Pomodoro
                    }}
                  />
                )}
                <PomodoroColumn
                  tasks={activeDailyTasks}
                  isLoading={loadingDaily}
                  dueDateFilter="all"
                  groupFutureByDate
                />
                <PlaybookMyStepsColumn />
                <CalendarColumn />
                <UpcomingDeadlinesColumn />
                {canSeeRecurringTasks && <RecurringClientTasksColumn />}
                <CompletedTasksColumn tasks={completedDailyTasks} isLoading={loadingDaily} />
              </div>
            </div>
            <StickyHorizontalScrollbar targetRef={scrollContainerRef} />
          </div>
        </TabsContent>

        {hasResponsibilityQueues && (
          <TabsContent value="responsibility">
            <TextQueuesView
              selectedBoardIds={
                selectedBoardIds.length > 0 
                  ? selectedBoardIds 
                  : (boards || []).map((b: any) => b.id).filter((id: any) => id != null)
              }
              onBoardFilterChange={handleBoardFilterChange}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
